import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';

export interface TokenPayload {
  exp: number;
  iat: number;
  sub: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles: string[];
    };
  };
  scope?: string;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
}

/** Profils admin reconnus dans l'application */
export type AdminProfile = 'SCA_SUPER_ADMIN' | 'SCA_IES_ADMIN' | null;

/**
 * Service de gestion des rôles et permissions.
 * Permet de vérifier les rôles d'un utilisateur à partir du token JWT.
 */
@Injectable({
  providedIn: 'root'
})
export class RolePermissionService {

  // Rôles requis pour accéder à l'application
  private readonly REQUIRED_APP_ROLE = ['SCA_IES_ADMIN', 'SCA_SUPER_ADMIN'];

  // Client ID dans Keycloak où se trouvent les profils d'admin
  private readonly CLIENT_ID = 'habilitation';

  constructor(private storage: StorageService) { }

  /**
   * Décode et parse le payload d'un token JWT.
   * @param token Le token JWT à décoder
   * @returns Le payload décodé ou null en cas d'erreur
   */
  private decodeToken(token: string): TokenPayload | null {
    try {
      const tokenParts = token.split('.');

      if (tokenParts.length !== 3) {
        console.error('Format de token JWT invalide');
        return null;
      }

      const base64Url = tokenParts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload) as TokenPayload;
    } catch (error) {
      console.error('Erreur lors du décodage du token:', error);
      return null;
    }
  }

  /**
   * Retourne le profil admin de l'utilisateur connecté en lisant
   * `resource_access.habilitation.roles` dans le token JWT.
   *
   * Priorité : SUPER_ADMIN > IES_ADMIN.
   *
   * @param token Token JWT optionnel (sinon récupéré depuis le storage)
   * @returns `'SUPER_ADMIN'`, `'IES_ADMIN'`, ou `null` si aucun profil connu
   */
  getAdminProfile(token?: string): AdminProfile {
    const accessToken = token || this.storage.getItem('access_token');

    if (!accessToken) {
      console.warn('getAdminProfile : aucun token disponible');
      return null;
    }

    const payload = this.decodeToken(accessToken);

    if (!payload) {
      return null;
    }

    const clientRoles: string[] =
      payload.resource_access?.[this.CLIENT_ID]?.roles ?? [];

    // Priorité SUPER_ADMIN
    if (clientRoles.some(r => r.toUpperCase() === 'SCA_SUPER_ADMIN')) {
      return 'SCA_SUPER_ADMIN';
    }

    if (clientRoles.some(r => r.toUpperCase() === 'SCA_IES_ADMIN')) {
      return 'SCA_IES_ADMIN';
    }

    console.warn(
      'getAdminProfile : aucun profil SUPER_ADMIN / IES_ADMIN trouvé dans',
      clientRoles
    );
    return null;
  }

  /**
   * Raccourci — vrai si l'admin connecté est un SUPER_ADMIN.
   */
  isSuperAdmin(token?: string): boolean {
    return this.getAdminProfile(token) === 'SCA_SUPER_ADMIN';
  }

  /**
   * Raccourci — vrai si l'admin connecté est un IES_ADMIN.
   */
  isIesAdmin(token?: string): boolean {
    return this.getAdminProfile(token) === 'SCA_IES_ADMIN';
  }

  /**
   * Récupère tous les rôles de l'utilisateur (realm + client).
   */
  getAllRoles(token?: string): { realmRoles: string[]; clientRoles: string[] } {
    const accessToken = token || this.storage.getItem('access_token');

    if (!accessToken) {
      return { realmRoles: [], clientRoles: [] };
    }

    const payload = this.decodeToken(accessToken);

    if (!payload) {
      return { realmRoles: [], clientRoles: [] };
    }

    const realmRoles = payload.realm_access?.roles || [];
    const clientRoles = payload.resource_access?.[this.CLIENT_ID]?.roles || [];

    return { realmRoles, clientRoles };
  }

  /**
   * Vérifie si l'utilisateur possède un rôle spécifique dans le client.
   */
  hasClientRole(roleName: string, token?: string): boolean {
    const accessToken = token || this.storage.getItem('access_token');

    if (!accessToken) {
      console.warn("Aucun token d'accès disponible");
      return false;
    }

    const payload = this.decodeToken(accessToken);

    if (!payload) {
      return false;
    }

    const clientRoles = payload.resource_access?.[this.CLIENT_ID]?.roles || [];
    return clientRoles.includes(roleName);
  }

  /**
   * Vérifie si l'utilisateur possède un rôle realm spécifique.
   */
  hasRealmRole(roleName: string, token?: string): boolean {
    const accessToken = token || this.storage.getItem('access_token');

    if (!accessToken) {
      return false;
    }

    const payload = this.decodeToken(accessToken);

    if (!payload) {
      return false;
    }

    const realmRoles = payload.realm_access?.roles || [];
    return realmRoles.includes(roleName);
  }

  /**
   * Vérifie si l'utilisateur possède AU MOINS UN des rôles spécifiés.
   */
  hasAnyRole(roles: string[], checkRealm: boolean = false, token?: string): boolean {
    return roles.some(role => {
      if (checkRealm) {
        return this.hasRealmRole(role, token) || this.hasClientRole(role, token);
      }
      return this.hasClientRole(role, token);
    });
  }

  /**
   * Vérifie si l'utilisateur possède TOUS les rôles spécifiés.
   */
  hasAllRoles(roles: string[], checkRealm: boolean = false, token?: string): boolean {
    return roles.every(role => {
      if (checkRealm) {
        return this.hasRealmRole(role, token) || this.hasClientRole(role, token);
      }
      return this.hasClientRole(role, token);
    });
  }

  /**
   * Vérifie si l'utilisateur peut accéder à l'application
   * (possède IES_ADMIN ou SUPER_ADMIN).
   */
  canAccessApplication(token?: string): boolean {
    return this.REQUIRED_APP_ROLE.some(role => this.hasClientRole(role, token));
  }

  /**
   * Récupère les informations utilisateur du token.
   */
  getUserInfo(token?: string): Partial<TokenPayload> | null {
    const accessToken = token || this.storage.getItem('access_token');

    if (!accessToken) {
      return null;
    }

    const payload = this.decodeToken(accessToken);

    if (!payload) {
      return null;
    }

    return {
      sub: payload.sub,
      email: payload.email,
      name: payload.name,
      preferred_username: payload.preferred_username,
      given_name: payload.given_name,
      family_name: payload.family_name,
      email_verified: payload.email_verified,
    };
  }

  /**
   * Vérifie si le token est encore valide.
   */
  isTokenValid(token?: string): boolean {
    const accessToken = token || this.storage.getItem('access_token');

    if (!accessToken) {
      return false;
    }

    const payload = this.decodeToken(accessToken);

    if (!payload || !payload.exp) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return now < payload.exp;
  }

  /**
   * Vérifie si l'utilisateur possède un rôle spécifique (client ou realm).
   */
  hasRole(roleName: string): boolean {
    return this.hasClientRole(roleName) || this.hasRealmRole(roleName);
  }

  /**
 * Extrait le sigle IES du token JWT de l'utilisateur connecté.
 * @returns le sigle IES ou null si non trouvé / non connecté
 */
  getCurrentUserIes(): string | null {
    return this.getCurrentUserIesCode();
  }

  getCurrentUserIesCode(): string | null {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.ies || null;
    } catch (e) {
      console.error('Impossible de décoder le token JWT', e);
      return null;
    }
  }
}